import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { Helmet } from 'react-helmet';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import Loader from '../../common/Loader';
import Avatar from '@material-ui/core/Avatar';
import DefaultAvatar from '../../../assets/icons/svgIcons/avatar.svg';
import { parseDate } from '../../../helpers/date';
import { routes } from '../../../assets/routes';
import { toggleBackButtonAction } from '../../../reactRedux/Actions/sideBarAction';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getFeedbackCommentsRequest,
  getFeedbackRequest,
  rejectFeedbackRequest,
  sendFeedbackCommentRequest,
  uploadFeedbackCommentAttachment
} from './api';
import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { getOrganizationFeaturesRequest } from '../../../helpers/api';
import { useTranslation } from '../../../i18n/LanguageProvider';
import { formatNumber } from '../../../i18n';

const FeedbackRequest = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useDesktopDisplay();
  const history = useHistory();

  const feedbackId = history.location.pathname.split('/')[2];
  const [comment, setComment] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);

  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));
  const setSideBarBackButton = (show) => dispatch(toggleBackButtonAction(show));

  const { data: orgFeatures } = useQuery(
    ['organizationFeatures'],
    getOrganizationFeaturesRequest
  );

  const hasFeedbackTitle = orgFeatures?.data?.find(
    (feature) => feature.feature_name === 'feedback_title'
  )?.active;

  const { isLoading: feedbackIsLoading, data: feedbackData } = useQuery(
    ['feedback', feedbackId],
    () => getFeedbackRequest(feedbackId),
    {
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const { isLoading: feedbackCommentsIsLoading, data: feedbackCommentsData } =
    useQuery(
      ['feedbackComments', feedbackId],
      () => getFeedbackCommentsRequest(feedbackId),
      {
        onError(err) {
          toast.error(err.response?.data?.detail);
        }
      }
    );

  useEffect(() => {
    setHeader(t('conversations.single'));
    setHeaderConfigs({ credits: false, exit: false, back: true, logo: false });
  }, []);

  useEffect(() => {
    setSideBarBackButton(true);
    return () => setSideBarBackButton(false);
  }, []);

  const sendFeedbackCommentRequestMutation = useMutation(
    sendFeedbackCommentRequest,
    {
      onSuccess: async (response) => {
        const feedbackCommentId = response?.data?.id;

        if (attachedFiles.length && feedbackCommentId) {
          try {
            await Promise.all(
              attachedFiles.map((file) =>
                uploadFeedbackCommentAttachment(feedbackCommentId, file)
              )
            );
          } catch (err) {
            toast.error(t('conversations.uploadFailed'));
          }
        }

        toast.success(t('conversations.replySent'));
        history.push(routes.FEEDBACKS);
      },
      onError(err) {
        toast.error(
          err.response?.data?.detail || t('conversations.replyFailed')
        );
      }
    }
  );

  const rejectFeedbackRequestMutation = useMutation(rejectFeedbackRequest, {
    onSuccess() {
      toast.success(t('conversations.replySent'));
      history.push(routes.FEEDBACKS);
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const removeFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const downloadFile = async (file) => {
    try {
      const response = await fetch(file.absolute_url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(t('conversations.downloadFailed'));
    }
  };

  return (
    <div className={classNames(styles.page, styles.full)}>
      <Helmet>
        <title>{t('conversations.single')}</title>
      </Helmet>
      {isDesktop && (
        <div className={styles.pageHeader}>{t('conversations.single')}</div>
      )}
      {feedbackIsLoading || feedbackCommentsIsLoading ? (
        <Loader size={40} />
      ) : (
        <>
          <div className={styles.wrapper}>
            <Avatar
              alt={feedbackData.sender?.first_name}
              src={feedbackData.sender?.avatar?.thumbnail || DefaultAvatar}
              className={styles.avatar}
            />
            <div className={styles.info}>
              <span>
                {`${feedbackData.sender?.first_name} ${feedbackData.sender?.last_name}`}
                {hasFeedbackTitle && feedbackData.title && (
                  <span className={styles.title}> - {feedbackData.title}</span>
                )}
              </span>
              <div className={styles.time}>
                {parseDate(feedbackData.created_at)}
              </div>
            </div>
            <div className={styles.vl} />
            <div className={styles.messageBlock}>
              <div className={styles.message} dir="auto">
                {feedbackData.message}
              </div>

              {feedbackData.attachments?.length > 0 && (
                <div className={styles.attachments}>
                  {feedbackData.attachments.map((file) => (
                    <button
                      key={file.id}
                      onClick={() => downloadFile(file)}
                      className={styles.attachmentItem}
                    >
                      <span className={styles.fileIcon}>📎</span>
                      <span className={styles.filename}>{file.filename}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {feedbackCommentsData.map((feedbackComment) => (
            <div key={feedbackComment.id} className={styles.wrapper}>
              <Avatar
                alt={feedbackComment.sender?.first_name}
                src={feedbackComment.sender?.avatar?.thumbnail || DefaultAvatar}
                className={styles.avatar}
              />
              <div className={styles.info}>
                <span>{`${feedbackComment.sender?.first_name} ${feedbackComment.sender?.last_name}`}</span>
                <div className={styles.time}>
                  {parseDate(feedbackComment.created_at)}
                </div>
              </div>
              <div className={styles.vl} />
              <div className={styles.messageBlock}>
                <div className={styles.message} dir="auto">
                  {feedbackComment.comment}
                </div>
                {hasFeedbackTitle && feedbackComment.attachments?.length > 0 && (
                  <div className={styles.attachments}>
                    {feedbackComment.attachments.map((file) => (
                      <button
                        key={file.id}
                        onClick={() => downloadFile(file)}
                        className={styles.attachmentItem}
                      >
                        <span className={styles.fileIcon}>📎</span>
                        <span className={styles.filename}>{file.filename}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className={styles.section}>
            <div className={styles.sectionTop}>
              <div className={styles.rejectButton}>
                {/* <Button
                  variant={'outlined'}
                  size={'small'}
                  onClick={() => setRejecting(!rejecting)}
                >
                  {rejecting ? 'پشیمون شدم' : 'نظری ندارم'}
                </Button> */}
              </div>
            </div>

            <div className={styles.sectionBody}>
              <TextField
                id="note-feedback"
                name="note-feedback"
                hiddenLabel
                multiline
                rows={4}
                fullWidth
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                }}
              />
            </div>
            <div className={styles.btnWrapper}>
              {hasFeedbackTitle && (
                <div className={styles.attachSection}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    style={{ marginTop: '1rem' }}
                  >
                    {t('conversations.attach')}
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={(e) => {
                        const selected = Array.from(e.target.files);
                        const total = attachedFiles.length + selected.length;

                        if (total > 3) {
                          toast.warn(
                            t('conversations.maxFiles', {
                              max: formatNumber(3)
                            })
                          );
                          return;
                        }

                        setAttachedFiles((prev) => [...prev, ...selected]);
                      }}
                    />
                  </Button>

                  {attachedFiles.length > 0 && (
                    <div className={styles.attachPreview}>
                      {attachedFiles.map((file, index) => (
                        <div key={index} className={styles.fileItem}>
                          <span>{file.name}</span>
                          <IconButton
                            size="small"
                            onClick={() => removeFile(index)}
                            aria-label={t('common.delete')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Button
                fullWidth
                variant="contained"
                disabled={
                  !comment ||
                  rejectFeedbackRequestMutation.isLoading ||
                  sendFeedbackCommentRequestMutation.isLoading
                }
                onClick={() => {
                  sendFeedbackCommentRequestMutation.mutate({
                    id: feedbackId,
                    comment: comment
                  });
                }}
              >
                {t('conversations.send')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FeedbackRequest;
